import jsep from "jsep";

type JsepExpression =
  | jsep.BinaryExpression
  | jsep.Literal
  | jsep.Identifier
  | jsep.UnaryExpression;

export function getQueryVariableNameByIndex(index: number) {
  return String.fromCharCode(97 + index);
}

export function evaluateFormula(formula: string | undefined, values: number[]) {
  if (!formula) {
    return {
      success: true as const,
      value: values[0] ?? 0,
    };
  }

  try {
    const ast = jsep(formula) as JsepExpression;
    const variables: Record<string, number> = {};

    // Create variables for each query result
    values.forEach((value, index) => {
      variables[getQueryVariableNameByIndex(index)] = Number(value);
    });

    // Evaluate the AST
    function evaluate(node: JsepExpression): number {
      if (node.type === "Literal" && typeof node.value === "number") {
        return node.value;
      }
      if (node.type === "Identifier" && node.name) {
        if (!(node.name in variables)) {
          throw new Error(`Variable ${node.name} not found`);
        }
        return variables[node.name] ?? 0;
      }
      if (node.type === "BinaryExpression" && node.operator) {
        const left = evaluate(node.left as JsepExpression);
        const right = evaluate(node.right as JsepExpression);
        switch (node.operator) {
          case "+":
            return left + right;
          case "-":
            return left - right;
          case "*":
            return left * right;
          case "/":
            return left / right;
          case "%":
            return left % right;
          case "^":
            return Math.pow(left, right);
          default:
            return 0;
        }
      }
      if (node.type === "UnaryExpression") {
        const argument = evaluate(node.argument as JsepExpression);
        return node.operator === "-" ? -argument : argument;
      }
      return 0;
    }

    const value = evaluate(ast);

    return {
      success: true as const,
      value,
    };
  } catch (error) {
    return {
      success: false as const,
      message: (error as Error).message,
    };
  }
}
